import { toast } from "@/components/common/Toast"
import { useTranslation } from "@/hooks/useTranslation/useTranslation"
import { useEffect, useRef } from "react"
import { useNavigate } from "react-router"
import type { Declaration, LifecycleStatus } from "../../../api/types"
import { declarationReviewPath } from "../routes"

/** The only fields this hook reads off a row — narrower than `Declaration` so
 *  a test can build one without the rest of the wire shape, and so this
 *  cannot start depending on some other field without the signature saying
 *  so. */
export type LifecycleToastRow = Pick<
  Declaration,
  "id" | "doc" | "lifecycleStatus"
>

const IN_FLIGHT: ReadonlySet<LifecycleStatus> = new Set([
  "pending",
  "processing",
])

/**
 * TOASTS ON OBSERVED LIFECYCLE TRANSITIONS — a declaration this mount watched
 * go from pending/processing to ready or failed, in-page, while the reviewer
 * stayed on the list. The notification centre already covers "ready for
 * review" server-side; this is a same-page complement for someone who is
 * already looking at the inbox and would otherwise only learn about it from
 * the next 3s poll silently reordering a row.
 *
 * MOUNT THIS ONCE, in the list (`DeclarationListV2`) — never in the review
 * page, which mounts the very same `useDeclarationsWithPolling` query and
 * would otherwise double-toast every transition it also observes.
 *
 * TRANSITIONS, NOT STATES. A `Map<id, status>` remembers what THIS MOUNT has
 * already seen for that id:
 *  - A row's FIRST observation (first load, or first time it appears in the
 *    loaded pages at all) is recorded silently — an inbox that already has
 *    ready/failed rows when the page opens must not toast for every one of
 *    them.
 *  - A row that DISAPPEARS from the loaded pages (facet change, pagination,
 *    delete) is forgotten outright — it is not "still pending" from this
 *    mount's point of view, so if it reappears later (even already at a
 *    terminal status) that is treated as a fresh first observation, not a
 *    transition spanning the gap.
 *  - Dedupe is the transition itself: an identical poll response processed
 *    twice sees `previous === current` and does nothing. There is deliberately
 *    NO "already toasted" set: a failed row can be retried in place
 *    (failed -> pending -> ready) and that second completion must toast too.
 *
 * KNOWN LIMIT: with a server-side lifecycle filter active (e.g. only
 * pending/processing), a row that turns ready drops out of the response and
 * is forgotten instead of toasted. The notification centre still delivers
 * "ready for review" for that case; this hook is deliberately a same-page
 * complement, not the system of record.
 */
export function useDeclarationLifecycleToasts(rows: LifecycleToastRow[]) {
  const { translate } = useTranslation()
  const navigate = useNavigate()
  const seenRef = useRef<Map<string, LifecycleStatus | undefined>>(new Map())

  useEffect(() => {
    const seen = seenRef.current
    const presentIds = new Set<string>()

    for (const row of rows) {
      presentIds.add(row.id)
      // `has` not `get !== undefined`: a legacy row can be SEEN with an
      // undefined status, which must not read as "never observed".
      const seenBefore = seen.has(row.id)
      const previous = seen.get(row.id)
      const current = row.lifecycleStatus
      const isTransition = seenBefore && previous !== current
      const wasInFlight = !!previous && IN_FLIGHT.has(previous)

      if (isTransition && wasInFlight) {
        if (current === "ready") {
          // Title stays short; the file name goes into the (truncating)
          // description line so a long name cannot wrap the pill.
          toast.success(translate("MC_DECL_TOAST_READY"), row.doc, {
            action: {
              label: translate("MC_DECL_TOAST_ACTION_OPEN"),
              onClick: () => navigate(declarationReviewPath(row.id)),
            },
          })
        } else if (current === "failed") {
          toast.error(translate("MC_DECL_TOAST_FAILED", { doc: row.doc }))
        }
      }

      seen.set(row.id, current)
    }

    // Forget rows no longer loaded — see "DISAPPEARS" above.
    for (const id of seen.keys()) {
      if (!presentIds.has(id)) {
        seen.delete(id)
      }
    }
  }, [rows, translate, navigate])
}
